import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2, Save, Loader2, LayoutTemplate, Check, Replace, BookOpen, Play } from 'lucide-react';
import { api, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import CoachShell from '@/components/CoachShell';
import { LoadingScreen } from '@/components/State';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import LibraryPickerModal from '@/components/LibraryPickerModal';
import { isValidYouTube } from '@/lib/youtube';

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const newExercise = (no) => ({ id: uid(), no, name: '', device: '', coach_note: '', sets: [{}, {}, {}] });
// New days start WITHOUT a language-bound name. The client renderer localizes an
// empty name to "Tag N" / "Day N" by index (Training.jsx), so no German default
// is ever persisted as the factual day name.
const newDay = () => ({ id: uid(), name: '', day_note: '', exercises: [newExercise(1)] });

export default function EditTraining() {
  const { id } = useParams();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState([]);
  const [meta, setMeta] = useState({ program_weeks: 1, days_per_week: 3, weeks: null });
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [applyingId, setApplyingId] = useState(null);

  const loadPlan = useCallback(async () => {
    try {
      const res = await api.get(`/training-plans/${id}?t=${Date.now()}`);
      const plan = res.data;
      const d = plan?.weeks?.[0]?.days || plan?.days || [];
      setDays(d.length ? d : [newDay()]);
      setMeta({ program_weeks: plan?.program_weeks || 1, days_per_week: plan?.days_per_week || d.length || 3, weeks: plan?.weeks || null });
    } catch (e) { toast.error(errMsg(e)); setDays([newDay()]); }
  }, [id]);

  useEffect(() => {
    (async () => {
      await loadPlan();
      try {
        const tp = await api.get('/plan-templates');
        setTemplates(tp.data?.templates || tp.data || []);
      } catch (e) { /* templates optional */ }
      setLoading(false);
    })();
  }, [loadPlan]);

  const applyTemplate = async (tpl) => {
    setApplyingId(tpl.id);
    try {
      await api.post(`/plan-templates/${tpl.id}/apply/${id}`);
      toast.success(`${tpl.name} ✓`);
      setTplOpen(false);
      await loadPlan();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setApplyingId(null); }
  };

  const tplDayCount = (tpl) => (tpl.weeks?.[0]?.days?.length) || tpl.days_per_week || (tpl.days?.length) || 0;
  const tplExCount = (tpl) => {
    const dd = tpl.weeks?.[0]?.days || tpl.days || [];
    return dd.reduce((s, d) => s + ((d.exercises || []).length), 0);
  };

  const updDay = (di, patch) => setDays((p) => p.map((d, i) => (i === di ? { ...d, ...patch } : d)));
  const updEx = (di, ei, patch) => setDays((p) => p.map((d, i) => i === di ? { ...d, exercises: d.exercises.map((e, j) => (j === ei ? { ...e, ...patch } : e)) } : d));
  const addEx = (di) => updDay(di, { exercises: [...days[di].exercises, newExercise(days[di].exercises.length + 1)] });
  const rmEx = (di, ei) => updDay(di, { exercises: days[di].exercises.filter((_, j) => j !== ei) });
  const setSetCount = (di, ei, n) => updEx(di, ei, { sets: Array.from({ length: Math.max(1, n) }, (_, k) => days[di].exercises[ei].sets[k] || {}) });

  // Library picker: exercise replace / add-from-library (embedded copy; template & library untouched)
  const [exPicker, setExPicker] = useState(null); // { di, ei|null, filter }

  const libFields = (lib) => ({
    name: lib.name,
    device: lib.device || '',
    muscle_group: lib.muscle_group || '',
    tags: lib.tags || [],
    exercise_library_id: lib.id,
    video_url: lib.video_url || '',
    video_type: lib.video_type || '',
  });

  const onPickExercise = (lib) => {
    if (!exPicker) return;
    const { di, ei } = exPicker;
    if (ei === null || ei === undefined) {
      // Add from library — new position, fresh training fields
      const ne = { ...newExercise(days[di].exercises.length + 1), ...libFields(lib) };
      updDay(di, { exercises: [...days[di].exercises, ne] });
    } else {
      // Replace — updEx spreads existing fields, so id/sets/coach_note/position are kept.
      // Clear any old override so the new library video resolves automatically.
      updEx(di, ei, { ...libFields(lib), video_override_url: '' });
    }
    setExPicker(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanDays = days.map((d) => ({ ...d, exercises: d.exercises.map((e, i) => ({ ...e, no: i + 1 })) }));
      // Preserve multi-week structure: replace week 1 days; keep other weeks
      let weeks = meta.weeks;
      if (weeks && weeks.length) {
        weeks = weeks.map((w, i) => (i === 0 ? { ...w, days: cleanDays } : w));
      } else {
        weeks = [{ number: 1, days: cleanDays }];
      }
      await api.post('/training-plans', {
        client_id: id, days: cleanDays, weeks,
        program_weeks: meta.program_weeks || weeks.length, days_per_week: cleanDays.length,
      });
      toast.success(t('edit.posted'));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <CoachShell back={`/coach/client/${id}`}><LoadingScreen /></CoachShell>;

  return (
    <CoachShell back={`/coach/client/${id}`} title={t('edit.training')}>
      <button
        onClick={() => setTplOpen(true)}
        data-testid="open-template-picker"
        className="mb-5 flex w-full items-center justify-center gap-2 rounded-xl border border-asgym-accent/40 bg-asgym-accent/10 py-4 text-sm font-extrabold tracking-wide text-asgym-accent transition-colors hover:bg-asgym-accent/15"
      >
        <LayoutTemplate size={17} /> {t('edit.applyTemplate')}
      </button>

      <div className="space-y-4">
        {days.map((d, di) => (
          <div key={d.id} className="as-card p-4" data-testid={`edit-day-${di}`}>
            <div className="mb-3 flex items-center gap-2">
              <input className="as-input-sm flex-1 font-bold" value={d.name} onChange={(e) => updDay(di, { name: e.target.value })} placeholder={t('edit.dayName')} />
              <button onClick={() => setDays(days.filter((_, i) => i !== di))} className="flex h-9 w-9 items-center justify-center rounded-lg border border-asgym-error/30 bg-asgym-error/10 text-asgym-error"><Trash2 size={15} /></button>
            </div>
            <div className="space-y-3">
              {d.exercises.map((ex, ei) => (
                <div key={ex.id} className="rounded-xl border border-white/[0.05] bg-black/20 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs font-black text-asgym-accent">{ei + 1}</span>
                    <input className="as-input-sm flex-1" value={ex.name} onChange={(e) => updEx(di, ei, { name: e.target.value })} placeholder={t('edit.exerciseName')} data-testid={`ex-name-${di}-${ei}`} />
                    {isValidYouTube(ex.video_override_url || ex.video_url) && (
                      <span className="flex h-7 items-center gap-1 rounded-md border border-asgym-accent/40 bg-asgym-accent/10 px-2 text-[10px] font-bold text-asgym-accent" title={t('lib.videoAvailable')}><Play size={11} fill="currentColor" /></span>
                    )}
                    <button onClick={() => setExPicker({ di, ei, filter: ex.muscle_group || 'all' })} data-testid={`replace-ex-${di}-${ei}`} title={t('edit.replaceExercise')} className="flex h-8 items-center gap-1 rounded-lg border border-white/[0.08] bg-white/[0.03] px-2.5 text-[11px] font-bold text-asgym-sub hover:text-asgym-accent"><Replace size={13} /> {t('edit.replaceShort')}</button>
                    <button onClick={() => rmEx(di, ei)} data-testid={`delete-ex-${di}-${ei}`} className="text-asgym-muted hover:text-asgym-error"><Trash2 size={15} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className="as-input-sm" value={ex.device || ''} onChange={(e) => updEx(di, ei, { device: e.target.value })} placeholder={t('edit.device')} />
                    <div className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-black/30 px-3">
                      <span className="text-[11px] font-bold text-asgym-muted">{t('edit.sets')}</span>
                      <input type="number" min="1" max="8" className="h-11 w-full bg-transparent text-sm text-white outline-none" value={(ex.sets || []).length} onChange={(e) => setSetCount(di, ei, parseInt(e.target.value, 10) || 1)} />
                    </div>
                  </div>
                  <input className="as-input-sm mt-2" value={ex.coach_note || ''} onChange={(e) => updEx(di, ei, { coach_note: e.target.value })} placeholder={t('edit.coachNote')} />
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => addEx(di)} className="as-btn-ghost h-10 flex-1 text-xs" data-testid={`add-ex-${di}`}><Plus size={14} /> {t('edit.addExercise')}</button>
              <button onClick={() => setExPicker({ di, ei: null, filter: 'all' })} className="as-btn-ghost h-10 flex-1 text-xs" data-testid={`add-ex-lib-${di}`}><BookOpen size={14} /> {t('edit.addFromLibrary')}</button>
            </div>
          </div>
        ))}
        <button onClick={() => setDays([...days, newDay()])} className="as-btn-ghost h-12 w-full" data-testid="add-day-btn"><Plus size={16} /> {t('edit.addDay')}</button>
      </div>

      <button onClick={save} disabled={saving} className="as-btn-primary sticky bottom-4 mt-6 w-full" data-testid="save-training-btn">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={16} /> {t('edit.savePlan')}</>}
      </button>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-asgym-surface text-asgym-text sm:max-w-lg" data-testid="template-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold tracking-tight text-white">
              {t('edit.chooseTemplate')}
            </DialogTitle>
            <DialogDescription className="text-asgym-sub">{t('edit.applyTemplateHint')}</DialogDescription>
          </DialogHeader>
          {templates.length === 0 ? (
            <p className="py-8 text-center text-sm text-asgym-sub">{t('edit.noTemplates')}</p>
          ) : (
            <div className="space-y-2.5 pt-2">
              {templates.map((tpl) => (
                <div key={tpl.id} data-testid={`template-option-${tpl.id}`} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-black/20 p-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-bold text-white">{tpl.name}</p>
                    <p className="text-[11px] text-asgym-muted">
                      {tpl.category ? `${tpl.category} · ` : ''}{tplDayCount(tpl)} {t('coach.days')} · {tplExCount(tpl)} {t('training.exercises')}
                    </p>
                  </div>
                  <button
                    onClick={() => applyTemplate(tpl)}
                    disabled={!!applyingId}
                    data-testid={`apply-template-${tpl.id}`}
                    className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-asgym-accent px-4 text-xs font-extrabold tracking-wide text-black transition-transform active:scale-95 disabled:opacity-50"
                  >
                    {applyingId === tpl.id ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />}
                    {t('edit.apply')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <LibraryPickerModal
        open={!!exPicker}
        onOpenChange={(o) => !o && setExPicker(null)}
        type="exercise"
        initialFilter={exPicker?.filter}
        onSelect={onPickExercise}
      />
    </CoachShell>
  );
}
